import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); refresh?.(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* retry */ }
      attempts.current += 1;
      if (attempts.current > 12) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params, refresh]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-bg px-6 text-center">
      <div className="absolute top-8 left-8"><Logo /></div>
      {status === "checking" && (
        <>
          <Loader2 className="animate-spin text-cyber" size={40} />
          <p className="mt-5 font-mono text-sm text-muted">Confirming your subscription…</p>
        </>
      )}
      {status === "paid" && (
        <div className="fade-up max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-secure/15 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="text-secure" size={34} />
          </div>
          <h1 className="font-display text-3xl font-extrabold text-ink">Subscription active</h1>
          <p className="mt-3 text-muted">Your AI Security Engineers are now deployed. Welcome to AICS AGENT.</p>
          <Button onClick={() => navigate("/dashboard")} data-testid="success-console-btn"
            className="mt-7 bg-cyber text-[#0B0E14] hover:bg-cyber-dim font-semibold h-11 px-6">
            Enter Console <ArrowRight size={17} className="ml-2" />
          </Button>
        </div>
      )}
      {["error", "timeout"].includes(status) && (
        <div className="max-w-md">
          <XCircle className="text-crit mx-auto mb-5" size={40} />
          <h1 className="font-display text-2xl font-bold text-ink">
            {status === "timeout" ? "Still processing" : "Something went wrong"}
          </h1>
          <p className="mt-3 text-muted">
            {status === "timeout" ? "Your payment is taking longer than usual. Check your billing shortly."
              : "We couldn't confirm this payment."}
          </p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-cyber text-[#0B0E14] font-semibold">
            Go to Console
          </Button>
        </div>
      )}
    </div>
  );
}
